import React, {
  Fragment,
  Suspense,
  useEffect,
  useSyncExternalStore,
} from "react";
import ReactDOM from "react-dom/client";

import { RouterProvider } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { ThemeProvider } from "styled-components";
import theme from "~/constants/theme";
import GlobalStyle from "~/globalStyle";

import "@fontsource-variable/inter/standard.css";
import { ToastContainer } from "~/components/toast";

import { routes } from "~/router/routes";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "~/translations/i18n";

import { analyticsSetup } from "./helpers/analytics";
import { datadogRumSetup, getCreateBrowserRouter } from "./helpers/datadogRum";
import { intercomKey } from "./helpers/environment";
import { ContextWrapper } from "./contexts";
import { ReactQueryProvider } from "./contexts/ReactQuery";
import tagManagerSetup from "./helpers/tagManagerSetup";
import SuspenseFallback from "~/components/layout/SuspenseFallback";
import { ErrorBoundary } from "~/components/ErrorBoundary";
import { confirmReadiness } from "~/api/base";
import { maintenance, startRecovery } from "~/helpers/maintenance";
import { MaintenancePage } from "~/components/maintenance";

analyticsSetup();
tagManagerSetup();

if (typeof window !== "undefined") {
  const runDatadog = () => {
    datadogRumSetup();
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(runDatadog);
  } else {
    setTimeout(runDatadog, 0);
  }
}

const createRouter = getCreateBrowserRouter();
const router = createRouter(routes);

/**
 * Takeover, not a redirect to a `/maintenance` route — the URL survives, so
 * recovery puts the user back exactly where they were.
 *
 * Mounted inside `ThemeProvider` (so it gets brand tokens) but wrapping the
 * router (so it can replace routed content without unmounting providers).
 * The app stays mounted and hidden rather than swapped out: unmounting the
 * router subtree would destroy a half-filled form, an open modal, scroll
 * position — hiding preserves all of that. `hidden` rather than a
 * `display: none` style, since the reset treats `[hidden]` as
 * `display: none !important` and also removes the subtree from the
 * accessibility tree, which a visual-only hide would not.
 */
function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const isDown = useSyncExternalStore(
    maintenance.subscribe,
    maintenance.getSnapshot
  );

  useEffect(() => {
    if (!isDown) return;
    return startRecovery(confirmReadiness);
  }, [isDown]);

  return (
    <>
      <div hidden={isDown}>{children}</div>
      {isDown && <MaintenancePage />}
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <IntercomProvider
        appId={intercomKey}
        autoBoot={false}
        apiBase={
          intercomKey
            ? `https://${intercomKey}.intercom-messenger.com`
            : undefined
        }>
        <ContextWrapper>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <MaintenanceGate>
              <Suspense fallback={<SuspenseFallback />}>
                <RouterProvider router={router} />
              </Suspense>
            </MaintenanceGate>
            <ToastContainer />
            <ReactQueryDevtools initialIsOpen={false} />
          </ThemeProvider>
        </ContextWrapper>
      </IntercomProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
